/**
 * Wave 5 (Slice A N-5) — shared NEW-password policy.
 *
 * Applies ONLY when a password is being set/changed. Existing passwords
 * are never re-validated against this policy (no retroactive lockouts):
 * sign-in and verify-password paths do not call this module.
 *
 * Policy: minimum 12 characters, not on the common-password blocklist
 * (case-insensitive), not a single repeated character.
 */
export const MIN_PASSWORD_LENGTH = 12;

const COMMON_PASSWORDS = new Set([
  'password', 'password1', 'password123', 'passw0rd', 'p@ssw0rd', 'p@ssword1',
  '123456', '12345678', '123456789', '1234567890', '12345', '123123', '111111',
  'qwerty', 'qwerty123', 'qwertyuiop', 'abc123', 'letmein', 'welcome', 'welcome1',
  'welcome123', 'admin', 'admin123', 'administrator', 'root', 'user', 'guest',
  'iloveyou', 'monkey', 'dragon', 'football', 'baseball', 'sunshine', 'master',
  'shadow', 'superman', 'batman', 'trustno1', 'hello123', 'charlie', 'thomas',
  'jordan', 'michael', 'hunter', 'buster', 'soccer', 'hockey', 'killer',
  'george', 'computer', 'michelle', 'jessica', 'pepper', '1234', '1234567',
  '12345678910', '000000', '654321', '987654321', 'qazwsx', 'qaz123', 'zxcvbnm',
  'asdfgh', 'asdf1234', '1q2w3e', '1q2w3e4r', 'q1w2e3r4', 'abcd1234', 'aaa111',
  'changeme', 'default', 'secret', 'secret123', 'login', 'login123', 'pass123',
  'pass1234', 'test1234', 'demo1234', 'isats123', 'isats1234', 'company123',
  'toronto', 'toronto123', 'canada', 'canada123', 'jamaica', 'jamaica123',
  'summer2024', 'summer2025', 'summer2026', 'winter2025', 'winter2026',
  'password2024', 'password2025', 'password2026', 'abcd12345', 'aaaa1111',
]);

export function isCommonPassword(password: string): boolean {
  return COMMON_PASSWORDS.has(password.toLowerCase());
}

// Both branches carry `message` (null on success) so callers can read
// it without discriminated-union narrowing — the repo tsconfig does
// not enable strict mode, under which `!result.ok` does not narrow.
export function validateNewPassword(password: unknown): { ok: true; message: null } | { ok: false; message: string } {
  if (typeof password !== 'string' || password.length === 0) {
    return { ok: false, message: 'Password is required.' };
  }
  if (password.length < MIN_PASSWORD_LENGTH) {
    return { ok: false, message: `Password must be at least ${MIN_PASSWORD_LENGTH} characters long.` };
  }
  if (isCommonPassword(password)) {
    return { ok: false, message: 'That password is too common. Please choose a less predictable password.' };
  }
  if (/^(.)\1+$/.test(password)) {
    return { ok: false, message: 'Password must not be a single repeated character.' };
  }
  return { ok: true, message: null };
}
